import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ChevronLeft, ChevronRight, ExternalLink, X, Menu,
  Gamepad2, Plus, Search, Building2, Trophy, Star, BookOpen, 
  Megaphone, Smartphone, Mail, Award, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

// Navigation items matching Home page
const NAV_ITEMS = [
  { id: "how-it-works", labelTop: "How It", labelBottom: "Works", icon: Gamepad2 },
  { id: "host-game", labelTop: "Host a", labelBottom: "Game", icon: Plus },
  { id: "find-games", labelTop: "Find", labelBottom: "Games", icon: Search },
  { id: "featured", labelTop: "Featured", labelBottom: "Games", icon: Trophy },
  { id: "membership", label: "Membership", icon: Star },
  { id: "discover", label: "Players", icon: Users, isPage: true, path: "/discover" },
  { id: "leaderboard", label: "Leaderboard", icon: Award, isPage: true, path: "/leaderboard" },
  { id: "rules", label: "Rules", icon: BookOpen, isPage: true, path: "/rules" },
  { id: "sponsors", label: "Sponsors", icon: Megaphone, isPage: true, path: "/sponsors" },
  { id: "venues", label: "Venues", icon: Building2, isPage: false },
  { id: "apps", label: "Apps", icon: Smartphone, isPage: true, path: "/apps" },
  { id: "contact", label: "Contact", icon: Mail, isPage: true, path: "/contact" },
];

const WALKTHROUGH_PASSWORD = "504to713";

// Demo user account
const DEMO_USER = {
  name: "Jane Doe",
  email: "jane.doe@example.com",
  avatar: null,
  membership: "Premium Member",
  verified: true,
};

// Walkthrough sections with screenshots and descriptions
const WALKTHROUGH_SECTIONS = [
  {
    id: "splash",
    title: "Splash Page",
    description: "The entry point to Board2Boston™. A premium, invitation-only experience begins here.",
    features: [
      "Animated golden logo entrance",
      "Premium brand presentation",
      "Automatic redirect to access code entry"
    ],
    screenshot: "/walkthrough/splash.png",
    demoUrl: "/"
  },
  {
    id: "access",
    title: "Access Code Entry",
    description: "Exclusive access control ensures only invited members can enter the platform.",
    features: [
      "Secure invitation code verification",
      "Multiple valid access codes supported",
      "Contact option for code requests"
    ],
    screenshot: "/walkthrough/access.png",
    demoUrl: "/access"
  },
  {
    id: "nda",
    title: "NDA & Membership Agreement",
    description: "Legal protection for both the platform and members with digital signature capture.",
    features: [
      "Digital signature collection",
      "Full NDA terms and conditions",
      "IP address and timestamp logging"
    ],
    screenshot: "/walkthrough/nda.png",
    demoUrl: "/nda"
  },
  {
    id: "registration",
    title: "User Registration",
    description: "Comprehensive member onboarding with gaming preferences.",
    features: [
      "Email and password authentication",
      "Gaming preference selection",
      "Profile customization options"
    ],
    screenshot: "/walkthrough/register.png",
    demoUrl: "/register"
  },
  {
    id: "home-hero",
    title: "Homepage - Hero Section",
    description: "The main dashboard welcoming verified members to the platform.",
    features: [
      "Dynamic welcome message",
      "Quick stats overview",
      "Primary action buttons for finding/hosting games"
    ],
    screenshot: "/walkthrough/home-hero.png",
    demoUrl: "/home"
  },
  {
    id: "find-games",
    title: "Find Games",
    description: "Advanced event discovery with powerful filtering capabilities.",
    features: [
      "Filter by game type, skill level, date",
      "Location-based search",
      "Entry fee range filtering",
      "Sort by date, participants, or fee"
    ],
    screenshot: "/walkthrough/find-games.png",
    demoUrl: "/home"
  },
  {
    id: "host-game",
    title: "Host a Game",
    description: "Create and manage your own gaming events and tournaments.",
    features: [
      "Event creation wizard",
      "Set rules and entry requirements",
      "Venue selection",
      "Participant management"
    ],
    screenshot: "/walkthrough/host-game.png",
    demoUrl: "/home"
  },
  {
    id: "profile",
    title: "User Profile",
    description: "Comprehensive player profile with stats, events, and social features.",
    features: [
      "Avatar upload and customization",
      "Player rating display",
      "Events hosted and attended",
      "Followers/Following counts"
    ],
    screenshot: "/walkthrough/profile.png",
    demoUrl: "/profile"
  },
  {
    id: "my-events",
    title: "My Events",
    description: "Track all your gaming activities in one place.",
    features: [
      "Events you're hosting",
      "Events you've registered for",
      "Event management tools",
      "RSVP cancellation"
    ],
    screenshot: "/walkthrough/my-events.png",
    demoUrl: "/profile"
  },
  {
    id: "challenges",
    title: "Challenge Players",
    description: "Direct player-to-player challenge system.",
    features: [
      "Send challenges to any player",
      "Accept or decline incoming challenges",
      "Propose game type and stakes",
      "Challenge history tracking"
    ],
    screenshot: "/walkthrough/challenges.png",
    demoUrl: "/profile"
  },
  {
    id: "leaderboard",
    title: "Leaderboard",
    description: "Competitive rankings based on player ratings.",
    features: [
      "Top-rated players display",
      "Rating-based rankings",
      "Player profiles linkable",
      "Game type filtering"
    ],
    screenshot: "/walkthrough/leaderboard.png",
    demoUrl: "/leaderboard"
  },
  {
    id: "discover",
    title: "Discover Players",
    description: "Find and connect with other players in the community.",
    features: [
      "Top-rated players showcase",
      "Recently active players",
      "Follow button integration",
      "Challenge directly from discovery"
    ],
    screenshot: "/walkthrough/discover.png",
    demoUrl: "/discover"
  },
  {
    id: "event-detail",
    title: "Event Detail Page",
    description: "Complete event information with chat and media gallery.",
    features: [
      "Full event details",
      "Participant list",
      "Event chat room",
      "Photo/video gallery"
    ],
    screenshot: "/walkthrough/event-detail.png",
    demoUrl: "/events"
  },
  {
    id: "tournaments",
    title: "Tournaments",
    description: "Organized competitive play with bracket systems.",
    features: [
      "Tournament registration",
      "Bracket visualization",
      "Match result tracking",
      "Winner announcements"
    ],
    screenshot: "/walkthrough/tournaments.png",
    demoUrl: "/tournaments"
  },
  {
    id: "venues",
    title: "Venue Partnerships",
    description: "Partner venue discovery and application system.",
    features: [
      "Browse partner venues",
      "Apply to become a partner",
      "Venue amenities display",
      "Commission structure"
    ],
    screenshot: "/walkthrough/venues.png",
    demoUrl: "/venue-partnership"
  },
  {
    id: "admin",
    title: "Admin Page Builder",
    description: "Drag-and-drop live content editing for administrators.",
    features: [
      "Visual page editor",
      "Real-time content updates",
      "Undo/Redo functionality",
      "Publish changes instantly"
    ],
    screenshot: "/walkthrough/admin.png",
    demoUrl: "/admin"
  },
  {
    id: "crm",
    title: "CRM Dashboard",
    description: "Customer relationship management for administrators.",
    features: [
      "User analytics",
      "NDA submission tracking",
      "Contact form submissions",
      "Admin login history"
    ],
    screenshot: "/walkthrough/crm.png",
    demoUrl: "/crm"
  }
];

const WalkthroughPage = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [currentSection, setCurrentSection] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === WALKTHROUGH_PASSWORD) {
      setAuthenticated(true);
      setError("");
    } else {
      setError("Invalid password");
    }
  };

  const handleNavClick = (item) => {
    if (item.isPage) {
      navigate(item.path);
    } else {
      navigate("/home", { state: { section: item.id } });
    }
    setMobileMenuOpen(false);
  };

  const nextSection = () => {
    if (currentSection < WALKTHROUGH_SECTIONS.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const openImageModal = (imageSrc) => {
    setModalImage(imageSrc);
    setShowModal(true);
  };

  // Password entry screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="walkthrough-password-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
              alt="Board2Boston"
              className="mx-auto mb-6"
              style={{ height: '120px' }}
            />
            <h1 className="font-bebas text-4xl text-white tracking-wider mb-2">INVESTOR WALKTHROUGH</h1>
            <p className="text-[#A1A1AA] font-ubuntu">Enter password to view the demo</p>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7a6200]" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter access password"
                className="w-full pl-12 pr-4 py-4 bg-[#0A0A0A] border border-[#7a6200]/30 rounded-lg text-white font-ubuntu placeholder:text-[#52525B] focus:border-[#7a6200] focus:outline-none"
                data-testid="walkthrough-password-input"
              />
            </div>
            {error && (
              <p className="text-red-400 text-sm font-ubuntu text-center">{error}</p>
            )}
            <Button
              type="submit"
              className="w-full btn-gold py-4 text-lg"
              data-testid="walkthrough-submit-btn"
            >
              ACCESS DEMO
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const section = WALKTHROUGH_SECTIONS[currentSection];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="walkthrough-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-[#7a6200]/30 bg-transparent backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo - clickable to go to Home */}
            <div 
              className="flex items-center gap-4 cursor-pointer flex-shrink-0"
              onClick={() => navigate("/home")}
              data-testid="walkthrough-logo"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
                alt="Board2Boston"
                style={{ height: '84px', width: 'auto' }}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center" style={{ gap: '2px' }}>
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="px-2 py-1 font-ubuntu text-xs transition-colors text-[#A1A1AA] hover:text-white text-center leading-tight whitespace-nowrap"
                  data-testid={`walkthrough-nav-${item.id}`}
                >
                  {item.labelTop ? (
                    <span className="flex flex-col items-center">
                      <span>{item.labelTop}</span>
                      <span>{item.labelBottom}</span>
                    </span>
                  ) : (
                    item.label
                  )}
                </button>
              ))}
            </nav>

            {/* Right section */}
            <div className="flex items-center gap-4">
              <div className="hidden md:block text-right">
                <h1 className="font-bebas text-lg text-white tracking-wider">INVESTOR WALKTHROUGH</h1>
                <p className="text-[#7a6200] text-xs font-ubuntu">Demo Account: {DEMO_USER.name}</p>
              </div>
              <span className="text-[#A1A1AA] font-ubuntu text-sm">
                {currentSection + 1} of {WALKTHROUGH_SECTIONS.length}
              </span>

              {/* Mobile Menu Toggle */}
              <button
                className="xl:hidden text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="xl:hidden border-t border-white/10 bg-[#0A0A0A]">
            <nav className="px-4 py-4 space-y-2">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded font-ubuntu text-sm text-[#A1A1AA] hover:bg-white/5"
                >
                  <item.icon className="w-5 h-5" />
                  {item.labelTop ? `${item.labelTop} ${item.labelBottom}` : item.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-24">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Navigation */}
          <div className="flex gap-2 mb-8 mt-3 overflow-x-auto pb-2 scrollbar-hide">
            {WALKTHROUGH_SECTIONS.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => setCurrentSection(idx)}
                className={`px-4 py-2 rounded-full font-ubuntu text-sm whitespace-nowrap transition-colors ${
                  idx === currentSection
                    ? "bg-[#7a6200] text-black"
                    : "bg-[#1A1A1A] text-[#A1A1AA] hover:bg-[#2A2A2A]"
                }`}
                data-testid={`walkthrough-nav-${s.id}`}
              >
                {s.title}
              </button>
            ))}
          </div>

          {/* Section Content */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Screenshot/Preview */}
            <div className="relative">
              <div 
                className="aspect-video bg-[#1A1A1A] rounded-xl border border-[#7a6200]/30 overflow-hidden cursor-pointer group"
                onClick={() => openImageModal(section.screenshot)}
              >
                {/* Placeholder for actual screenshots - In production, these would be actual screenshots */}
                <div className="w-full h-full flex flex-col items-center justify-center p-8">
                  <div className="text-[#7a6200] font-bebas text-3xl mb-4">{section.title}</div>
                  <p className="text-[#A1A1AA] text-center font-ubuntu text-sm mb-6">{section.description}</p>
                  <div className="w-full max-w-md mx-auto border border-[#7a6200]/20 rounded-lg p-4 bg-[#0A0A0A]">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="ml-4 text-[#52525B] text-xs font-mono">{section.demoUrl}</span>
                    </div>
                    <div className="space-y-2">
                      {section.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#7a6200]" />
                          <span className="text-[#A1A1AA] text-xs font-ubuntu">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Zoom hint */}
                <div className="absolute bottom-4 right-4 bg-black/60 px-3 py-1 rounded text-[#7a6200] text-xs font-ubuntu opacity-0 group-hover:opacity-100 transition-opacity">
                  Click to expand
                </div>
              </div>
            </div>

            {/* Section Details */}
            <div className="space-y-6">
              <div>
                <h2 className="font-bebas text-4xl text-white tracking-wider mb-2">{section.title}</h2>
                <p className="text-[#A1A1AA] font-ubuntu text-lg">{section.description}</p>
              </div>

              <div className="bg-[#1A1A1A] rounded-xl border border-[#7a6200]/30 p-6">
                <h3 className="font-bebas text-xl text-[#7a6200] tracking-wider mb-4">KEY FEATURES</h3>
                <ul className="space-y-3">
                  {section.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#7a6200]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[#7a6200] text-xs font-bold">{idx + 1}</span>
                      </div>
                      <span className="text-white font-ubuntu">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Demo Account Info */}
              <div className="bg-[#0A0A0A] rounded-xl border border-[#7a6200]/30 p-6">
                <h3 className="font-bebas text-lg text-white tracking-wider mb-3">DEMO ACCOUNT</h3>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
                    <span className="text-[#7a6200] font-bebas text-xl">JD</span>
                  </div>
                  <div>
                    <p className="text-white font-ubuntu font-medium">{DEMO_USER.name}</p>
                    <p className="text-[#A1A1AA] text-sm font-ubuntu">{DEMO_USER.email}</p>
                    <p className="text-[#7a6200] text-xs font-ubuntu">{DEMO_USER.membership}</p>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-4">
                <Button
                  onClick={prevSection}
                  disabled={currentSection === 0}
                  className="flex-1 btn-outline-gold py-4 disabled:opacity-30"
                  data-testid="walkthrough-prev-btn"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={nextSection}
                  disabled={currentSection === WALKTHROUGH_SECTIONS.length - 1}
                  className="flex-1 btn-gold py-4 disabled:opacity-30"
                  data-testid="walkthrough-next-btn"
                >
                  Next
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Navigation Grid */}
          <div className="mt-12">
            <h3 className="font-bebas text-2xl text-white tracking-wider mb-6">ALL SECTIONS</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {WALKTHROUGH_SECTIONS.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => setCurrentSection(idx)}
                  className={`p-4 rounded-xl border transition-all ${
                    idx === currentSection
                      ? "bg-[#7a6200]/20 border-[#7a6200]"
                      : "bg-[#1A1A1A] border-[#7a6200]/20 hover:border-[#7a6200]/50"
                  }`}
                >
                  <span className={`font-bebas text-sm tracking-wider ${
                    idx === currentSection ? "text-[#7a6200]" : "text-white"
                  }`}>
                    {s.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="fixed bottom-0 left-0 right-0 border-t border-[#7a6200]/30 bg-[#050505]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={prevSection}
              disabled={currentSection === 0}
              variant="ghost"
              className="text-[#A1A1AA] hover:text-white disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Previous
            </Button>
            <div className="flex gap-1">
              {WALKTHROUGH_SECTIONS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSection(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentSection ? "bg-[#7a6200]" : "bg-[#7a6200]/30"
                  }`}
                />
              ))}
            </div>
            <Button
              onClick={nextSection}
              disabled={currentSection === WALKTHROUGH_SECTIONS.length - 1}
              variant="ghost"
              className="text-[#A1A1AA] hover:text-white disabled:opacity-30"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </div>
      </footer>

      {/* Image Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <button
            onClick={() => setShowModal(false)}
            className="absolute top-4 right-4 text-white hover:text-[#7a6200] transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-5xl w-full">
            <div className="aspect-video bg-[#1A1A1A] rounded-xl border border-[#7a6200]/30 overflow-hidden">
              <div className="w-full h-full flex flex-col items-center justify-center p-12">
                <div className="text-[#7a6200] font-bebas text-5xl mb-6">{section.title}</div>
                <p className="text-[#A1A1AA] text-center font-ubuntu text-lg mb-8 max-w-2xl">{section.description}</p>
                <div className="w-full max-w-xl mx-auto border border-[#7a6200]/20 rounded-lg p-6 bg-[#0A0A0A]">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="ml-4 text-[#52525B] text-sm font-mono">{section.demoUrl}</span>
                  </div>
                  <div className="space-y-3">
                    {section.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#7a6200]" />
                        <span className="text-white font-ubuntu">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalkthroughPage;
